import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MultiDatePicker from "react-multi-date-picker";
import "./rescheduleappt.css";
import { rescheduleAppointmentAdmin } from "../../../../services/admin";
import { getTimeImage } from "../../../../utils/utils";
import PreviewModal from "../../calendar/previewModal";
import Button from "../../../../components/button";
import { setBookingDate, setBookingTime } from "../../../../reducer/clientsSlice";
import { slotsArray } from "../../calendar/data";

// Admin reschedule of a 1-on-1 appointment.
// Normal mode: pick an open slot from the client booking view.
// Override mode: pick any date/time; medium blocks and times outside open slots are
// allowed after the admin confirms. Double bookings are always refused by the API.
// The override only moves this appointment - the medium's schedule is not changed.
const OVERRIDE_REASONS = ["block", "unavailable"];

const firstErrorMessages = (error, fallback) => {
    if (error && typeof error === "object") {
        return Object.values(error).map((messages) => messages[0]);
    }
    return [fallback];
};

export default function ReScheduleAppt({
    reschedeId,
    reschedeData,
    getAllAppointmentsList,
    activeTab,
    setReschedeId,
}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bookingdate, bookingtime } = useSelector((state) => state.client);

    const [isOverride, setIsOverride] = useState(false);
    const [overrideDate, setOverrideDate] = useState("");
    const [overrideTime, setOverrideTime] = useState("");
    const [checkResult, setCheckResult] = useState(null);
    const [errors, setErrors] = useState([]);
    const [overridePrompt, setOverridePrompt] = useState("");
    const [reason, setReason] = useState("");
    const [notifyClient, setNotifyClient] = useState(true);
    const [notifyMedium, setNotifyMedium] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [preview, setPreview] = useState(false);
    const [activesession_id, setActivesession_id] = useState("");
    const [activeSubSessionId, setActiveSubSessionId] = useState("");
    const [is_group_session, setis_group_session] = useState(null);
    const [client_id, setclient_id] = useState("");

    const appointmentId = reschedeId && reschedeId.id;
    const timezones = (reschedeData && reschedeData.timezone) || {};
    const selectedDate = isOverride ? overrideDate : bookingdate;
    const selectedTime = isOverride ? overrideTime : bookingtime;
    const needsOverride = !!(checkResult && checkResult.requires_override);

    const resetState = () => {
        dispatch(setBookingTime(""));
        dispatch(setBookingDate(""));
        setIsOverride(false);
        setOverrideDate("");
        setOverrideTime("");
        setCheckResult(null);
        setErrors([]);
        setOverridePrompt("");
        setReason("");
        setNotifyClient(true);
        setNotifyMedium(true);
        setSubmitting(false);
    };

    // A different appointment was opened.
    useEffect(() => {
        if (appointmentId) {
            resetState();
            setOverrideDate(timezones.client_timezone_date || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    // A slot was picked in the client booking view.
    useEffect(() => {
        if (bookingtime !== "" && preview) {
            window.loadModal2("#prvvvvv", "hide");
            setPreview(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingdate, bookingtime]);

    // Evaluate the chosen time: medium time, blocks, cautions. Nothing is saved.
    useEffect(() => {
        setCheckResult(null);
        setErrors([]);
        setOverridePrompt("");
        if (!appointmentId || !selectedDate || !selectedTime) {
            return;
        }
        let stale = false;
        rescheduleAppointmentAdmin(appointmentId, {
            date: selectedDate,
            time: selectedTime,
            check: 1,
        })
            .then((response) => {
                if (stale || !response || !response.data) {
                    return;
                }
                if (response.data.status) {
                    setCheckResult(response.data.data);
                } else {
                    setErrors(firstErrorMessages(response.data.error, response.data.message));
                }
            })
            .catch(() => { });
        return () => {
            stale = true;
        };
    }, [appointmentId, selectedDate, selectedTime]);

    const closeModal = () => {
        resetState();
        setReschedeId("");
        window.loadModal2("#ReScheduleAppt", "hide");
        window.removeBackdrop();
    };

    const submit = (forceOverride) => {
        if (!selectedDate || !selectedTime) {
            setErrors(["Please select a date and time"]);
            return;
        }
        if (submitting) {
            return;
        }
        setSubmitting(true);
        rescheduleAppointmentAdmin(appointmentId, {
            date: selectedDate,
            time: selectedTime,
            is_override: isOverride || forceOverride ? 1 : 0,
            reason: reason.trim() || null,
            notify_client: notifyClient ? 1 : 0,
            notify_medium: notifyMedium ? 1 : 0,
        })
            .then((response) => {
                setSubmitting(false);
                if (!response || !response.data) {
                    setErrors(["Something went wrong. Please try again."]);
                    return;
                }
                if (response.data.status) {
                    toast.success(response.data.message, { position: "top-center" });
                    closeModal();
                    getAllAppointmentsList({ page: 1, rowsPerPage: 10 }, activeTab);
                    return;
                }
                if (OVERRIDE_REASONS.includes(response.data.error)) {
                    setCheckResult(response.data.data);
                    setOverridePrompt(response.data.message);
                    return;
                }
                setErrors(firstErrorMessages(response.data.error, response.data.message));
            })
            .catch(() => setSubmitting(false));
    };

    const openPreviewModal = (item) => {
        setclient_id(item.client_id);
        const params = new URLSearchParams();
        params.append("id", item.medium_id);
        navigate({ pathname: "/appointments", search: params.toString() });
        setActiveSubSessionId(item.subsession_table_id);
        setis_group_session(item.is_group_session);
        setActivesession_id(item.session_type);
        setPreview(true);
        localStorage.removeItem("showpreview");
        localStorage.removeItem("showpreviewValue");
        window.loadModal2("#prvvvvv", "show");
    };

    const toggleOverride = (checked) => {
        setIsOverride(checked);
        if (checked) {
            setOverrideDate(bookingdate || overrideDate || timezones.client_timezone_date || "");
            setOverrideTime(bookingtime || "");
        }
    };

    const clientTimezone = (checkResult && checkResult.client.timezone) || timezones.client_timezone;
    const mediumTimezone = (checkResult && checkResult.medium.timezone) || timezones.medium_timezone;

    return (
        <>
            <div className="register-medium">
                <div
                    className="modal fade "
                    id="ReScheduleAppt"
                    data-backdrop="static"
                    role="dialog"
                >
                    <div className="width-unset  modal-dialog advanceConfirmbooking modal-lg  modal-dialog-preview height-unset">
                        <div className="modal-content p-5">
                            <div className="modal-body">
                                <div className="schedule-popup">
                                    <h3 className="mb-2 mt-2 text-center">
                                        Re-Schedule Appointment ({"2A" + (appointmentId || "") + "X"})
                                    </h3>
                                    {reschedeId && reschedeId.date && (
                                        <p className="text-center font-size-14 mb-4">
                                            Currently: {reschedeId.date} {reschedeId.time}
                                        </p>
                                    )}
                                    {errors.map((item) => (
                                        <div key={item} className="eroorbox red">{item}</div>
                                    ))}
                                    {checkResult && !needsOverride && (
                                        <div className="eroorbox bg-green">Slot is Available</div>
                                    )}
                                    {checkResult && checkResult.cautions.map((caution) => (
                                        <div key={caution.type} className="eroorbox warning-color">
                                            {caution.message}
                                        </div>
                                    ))}
                                    {(overridePrompt || (needsOverride && !isOverride)) && (
                                        <div className="eroorbox warning-color">
                                            {overridePrompt || "This time needs an override. Would you still like to reschedule?"}
                                            {" "}The medium's schedule and blocks will not be changed.
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex justify-content-center mb-3">
                                    <label className="d-flex align-items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="mr-2 CheckBox-custom"
                                            checked={isOverride}
                                            onChange={(e) => toggleOverride(e.target.checked)}
                                        />
                                        <span>Allow any time (override medium blocks)</span>
                                    </label>
                                </div>

                                <div className="reschedule-grid">
                                    <div className="reschedule-row">
                                        <div className="reschedule-cell reschedule-role">Client</div>
                                        {isOverride ? (
                                            <>
                                                <div className="reschedule-field">
                                                    <label>Date</label>
                                                    <MultiDatePicker
                                                        inputClass="reschedule-input"
                                                        format="YYYY-MM-DD"
                                                        placeholder="Select date"
                                                        minDate={timezones.client_timezone_date || new Date()}
                                                        value={overrideDate || null}
                                                        onChange={(date) => setOverrideDate(date ? date.format("YYYY-MM-DD") : "")}
                                                    />
                                                </div>
                                                <div className="reschedule-field">
                                                    <label htmlFor="rescheduleTime">Time</label>
                                                    <div className="d-flex-appointment align-items-center justify-content-center timeAppointment">
                                                        <select
                                                            id="rescheduleTime"
                                                            name="time"
                                                            className="p-2 reschedule-input"
                                                            value={overrideTime}
                                                            onChange={(e) => setOverrideTime(e.target.value)}
                                                        >
                                                            <option value="" disabled>
                                                                Time
                                                            </option>
                                                            {slotsArray.map((item, index) => (
                                                                <option
                                                                    key={`${item.val}-${index}`}
                                                                    className="moon-create-app star-imh-moon"
                                                                    value={item.time}
                                                                >
                                                                    {item.change === "NO" ? "12:00 AM" : item.time}{" "}
                                                                    {getTimeImage(item.time) ? "🌙" : ""}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="reschedule-cell">
                                                <Button
                                                    status={0}
                                                    type="button"
                                                    className="resend-btn w-unset height-35 min-width-300px m-0"
                                                    title={
                                                        bookingdate && bookingtime
                                                            ? `${bookingdate}, ${bookingtime} (change)`
                                                            : "Click here to select date & time"
                                                    }
                                                    onClick={() => openPreviewModal(reschedeId)}
                                                />
                                            </div>
                                        )}
                                        <div className="reschedule-cell reschedule-timezone">{clientTimezone}</div>
                                    </div>

                                    <div className="reschedule-row">
                                        <div className="reschedule-cell reschedule-role">Medium</div>
                                        <div className="reschedule-cell reschedule-value">
                                            {checkResult
                                                ? `${checkResult.medium.date}, ${checkResult.medium.time}`
                                                : "-"}
                                        </div>
                                        <div className="reschedule-cell reschedule-timezone">{mediumTimezone}</div>
                                    </div>
                                </div>

                                <div className="ml-5 mr-5 mb-3">
                                    <label className="font-size-17" htmlFor="rescheduleReason">
                                        Reason (optional, kept in the appointment log)
                                    </label>
                                    <textarea
                                        id="rescheduleReason"
                                        className="d-block p-2 w-100"
                                        rows={2}
                                        maxLength={500}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </div>

                                <div className="d-flex justify-content-center mb-3">
                                    <label className="d-flex align-items-center cursor-pointer mr-4">
                                        <input
                                            type="checkbox"
                                            className="mr-2 CheckBox-custom"
                                            checked={notifyClient}
                                            onChange={(e) => setNotifyClient(e.target.checked)}
                                        />
                                        <span>Email client</span>
                                    </label>
                                    <label className="d-flex align-items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="mr-2 CheckBox-custom"
                                            checked={notifyMedium}
                                            onChange={(e) => setNotifyMedium(e.target.checked)}
                                        />
                                        <span>Email medium</span>
                                    </label>
                                </div>

                                <div className="confirm-delete-wrapper ">
                                    <div className="confirm-btn-box">
                                        <div className="register-medium-confirnation" onClick={closeModal}>
                                            Cancel
                                        </div>
                                        <div
                                            className={`register-medium-confirnation ${submitting ? "opacity-50" : ""}`}
                                            onClick={() => submit(needsOverride || overridePrompt !== "")}
                                        >
                                            {needsOverride || overridePrompt ? "Reschedule anyway" : "Update"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {preview && (
                <PreviewModal
                    user_type=""
                    is_group_session={is_group_session}
                    setPreview={setPreview}
                    activesession_id={activesession_id}
                    setActivesession_id={setActivesession_id}
                    type={"admin"}
                    preferncesSection={null}
                    activeSubSessionId={activeSubSessionId}
                    client_id={client_id}
                />
            )}
        </>
    );
}
